import React from 'react';
import {
    ScatterChart,
    Scatter,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell,
    ReferenceLine
} from 'recharts';
import './SourceMatrix.css';

/**
 * SourceMatrix Component
 * Scatter plot showing source effectiveness (volume vs speed)
 */
function SourceMatrix({ data, loading }) {
    if (loading) {
        return (
            <div className="chart-container chart-loading">
                <div className="chart-loading-spinner"></div>
            </div>
        );
    }

    if (!data || data.length === 0) {
        return (
            <div className="chart-container chart-empty">
                <p>No source data available</p>
            </div>
        );
    }

    const getQuadrantColor = (quadrant) => {
        switch (quadrant) {
            case 'star':
                return '#27AE60'; // Green - Stars (fast + high volume)
            case 'workhorse':
                return '#F39C12'; // Yellow - Workhorses (slow but high volume)
            case 'quickwin':
                return '#3498DB'; // Blue - Quick wins (fast but low volume)
            case 'underperformer':
            default:
                return '#95A5A6'; // Gray - Underperformers
        }
    };

    // Calculate medians for reference lines
    const medianDays = data.reduce((sum, d) => sum + d.avgDaysToHire, 0) / data.length;
    const medianHires = data.reduce((sum, d) => sum + d.totalHires, 0) / data.length;

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const d = payload[0].payload;
            return (
                <div className="chart-tooltip">
                    <p className="chart-tooltip-label">{d.source}</p>
                    <p className="chart-tooltip-item">
                        Total Hires: <strong>{d.totalHires}</strong>
                    </p>
                    <p className="chart-tooltip-item">
                        Avg Days to Hire: <strong>{d.avgDaysToHire}</strong>
                    </p>
                    <p className="chart-tooltip-item">
                        Conversion Rate: <strong>{d.conversionRate}%</strong>
                    </p>
                    <p className="chart-tooltip-item">
                        Total Candidates: <strong>{d.totalCandidates}</strong>
                    </p>
                    <p className={`chart-tooltip-quadrant quadrant-${d.quadrant}`}>
                        {d.quadrant === 'star' && 'Star (Fast & High Volume)'}
                        {d.quadrant === 'workhorse' && 'Workhorse (Slow but High Volume)'}
                        {d.quadrant === 'quickwin' && 'Quick Win (Fast but Low Volume)'}
                        {d.quadrant === 'underperformer' && 'Needs Improvement'}
                    </p>
                </div>
            );
        }
        return null;
    };

    // Calculate bubble sizes based on conversion rate
    const maxConversion = Math.max(...data.map(d => d.conversionRate));
    const chartData = data.map(d => ({
        ...d,
        bubbleSize: 50 + (d.conversionRate / maxConversion) * 150
    }));

    return (
        <div className="chart-container source-matrix">
            <div className="chart-header">
                <h3 className="chart-title">Source Effectiveness</h3>
                <div className="source-legend">
                    <span className="source-legend-item">
                        <span className="legend-dot" style={{ background: '#27AE60' }}></span>
                        Stars
                    </span>
                    <span className="source-legend-item">
                        <span className="legend-dot" style={{ background: '#F39C12' }}></span>
                        Workhorses
                    </span>
                    <span className="source-legend-item">
                        <span className="legend-dot" style={{ background: '#3498DB' }}></span>
                        Quick Wins
                    </span>
                    <span className="source-legend-item">
                        <span className="legend-dot" style={{ background: '#95A5A6' }}></span>
                        Underperformers
                    </span>
                </div>
            </div>

            <ResponsiveContainer width="100%" height={300}>
                <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                        type="number"
                        dataKey="avgDaysToHire"
                        name="Days to Hire"
                        tick={{ fontSize: 12, fill: '#7F8C8D' }}
                        axisLine={{ stroke: '#e5e7eb' }}
                        label={{ value: 'Avg Days to Hire (faster →)', position: 'insideBottom', offset: -5, fill: '#7F8C8D', fontSize: 11 }}
                        reversed
                    />
                    <YAxis
                        type="number"
                        dataKey="totalHires"
                        name="Total Hires"
                        tick={{ fontSize: 12, fill: '#7F8C8D' }}
                        axisLine={{ stroke: '#e5e7eb' }}
                        label={{ value: 'Total Hires', angle: -90, position: 'insideLeft', fill: '#7F8C8D', fontSize: 11 }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <ReferenceLine
                        x={medianDays}
                        stroke="#e5e7eb"
                        strokeDasharray="5 5"
                    />
                    <ReferenceLine
                        y={medianHires}
                        stroke="#e5e7eb"
                        strokeDasharray="5 5"
                    />
                    <Scatter
                        name="Sources"
                        data={chartData}
                        shape="circle"
                    >
                        {chartData.map((entry, index) => (
                            <Cell
                                key={`cell-${index}`}
                                fill={getQuadrantColor(entry.quadrant)}
                                fillOpacity={0.7}
                                stroke={getQuadrantColor(entry.quadrant)}
                                strokeWidth={2}
                                r={Math.sqrt(entry.bubbleSize)}
                            />
                        ))}
                    </Scatter>
                </ScatterChart>
            </ResponsiveContainer>

            <div className="source-list">
                {chartData.map((source, index) => (
                    <div key={index} className={`source-item quadrant-${source.quadrant}`}>
                        <span className="source-name">{source.source}</span>
                        <span className="source-stats">
                            {source.totalHires} hires | {source.avgDaysToHire} days | {source.conversionRate}%
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default SourceMatrix;
